import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {Element[]} */
  #purchaseAreaTargets = [];

  /** @type {number | null} */
  #setupRetryTimeout = null;

  /** @type {number | null} */
  #visibilityFrame = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    window.addEventListener('scroll', this.#requestVisibilityCheck, { passive: true, signal });
    window.addEventListener('resize', this.#requestVisibilityCheck, { signal });

    this.#setupIntersectionObserver();
    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
    if (this.#setupRetryTimeout) {
      clearTimeout(this.#setupRetryTimeout);
    }
    if (this.#visibilityFrame) {
      cancelAnimationFrame(this.#visibilityFrame);
    }
  }

  /**
   * Sets up observers for the real product price and buy buttons area.
   */
  #setupIntersectionObserver() {
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();

    const productForm = this.#getProductForm();
    if (!productForm) {
      this.#queueObserverSetupRetry();
      return;
    }

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) {
      this.#queueObserverSetupRetry();
      return;
    }

    this.#purchaseAreaTargets = this.#getPurchaseAreaTargets(buyButtonsBlock);

    this.#buyButtonsIntersectionObserver = new IntersectionObserver(() => {
      this.#requestVisibilityCheck();
    });

    this.#purchaseAreaTargets.forEach((target) => this.#buyButtonsIntersectionObserver?.observe(target));

    // In themes migrated from 2.0, the footer element doesn't always exist.
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (footer) {
      this.#mainBottomObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          this.#hiddenByBottom = !!entry?.isIntersecting;
          this.#requestVisibilityCheck();
        },
        {
          rootMargin: '200px 0px 0px 0px',
        }
      );
      this.#mainBottomObserver.observe(footer);
    }

    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    this.#requestVisibilityCheck();
  }

  #queueObserverSetupRetry() {
    if (this.#setupRetryTimeout) return;

    this.#setupRetryTimeout = window.setTimeout(() => {
      this.#setupRetryTimeout = null;
      this.#setupIntersectionObserver();
    }, 100);
  }

  /**
   * Watches the main buy buttons so the sticky bar stays hidden whenever
   * the real add-to-cart controls are visible.
   * @param {Element} buyButtonsBlock
   * @returns {Element[]}
   */
  #getPurchaseAreaTargets(buyButtonsBlock) {
    return [buyButtonsBlock];
  }

  #requestVisibilityCheck = () => {
    if (this.#visibilityFrame) return;

    this.#visibilityFrame = requestAnimationFrame(() => {
      this.#visibilityFrame = null;
      this.#updateStickyVisibility();
    });
  };

  #updateStickyVisibility() {
    if (!this.#purchaseAreaTargets.length) {
      this.#hideStickyBar();
      return;
    }

    const purchaseAreaVisible = this.#purchaseAreaTargets.some((target) => this.#isElementInViewport(target));
    const shouldShow = !purchaseAreaVisible && !this.#hiddenByBottom;

    if (shouldShow) {
      this.#showStickyBar();
    } else {
      this.#hideStickyBar();
    }
  }

  /**
   * @param {Element} element
   */
  #isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');

    if (newStickyAddToCart) {
      const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
      if (!newStickyBar) return;

      const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
      const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

      morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

      this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
      this.dataset.variantAvailable = variantAvailable;

      if (variant && variant.id) {
        this.dataset.currentVariantId = variant.id;
      }

      const productForm = this.#getProductForm();
      if (productForm) {
        this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
      }

      if (variant == null) {
        this.#handleVariantUnavailable();
      }

      this.#updateButtonText();
      return;
    }

    if (variant?.price != null) {
      this.#updatePriceFromVariant(variant);

      if (variant.id) {
        this.dataset.currentVariantId = String(variant.id);
      }

      this.dataset.variantAvailable = variant.available ? 'true' : 'false';

      if (this.refs.addToCartButton) {
        this.refs.addToCartButton.disabled = !variant.available;
      }

      if (variant.title) {
        const variantLabel = this.refs.stickyBar.querySelector('#sticky-variant-label');
        const selectLabel = this.refs.stickyBar.querySelector('#sticky-select-size-label');
        if (variantLabel) variantLabel.textContent = variant.title;
        if (selectLabel) selectLabel.textContent = variant.title;
      }
    }
  };

  /**
   * @param {Record<string, unknown>} variant
   */
  #updatePriceFromVariant(variant) {
    const priceBlock = this.refs.stickyBar.querySelector('#sticky-price-block');
    if (!priceBlock) return;

    const container = priceBlock.querySelector('[ref="priceContainer"]');
    const discount = Number(container?.dataset.discount) || Number(this.dataset.discount) || 0;
    const info = this.#calcPriceInfo(variant, discount);
    const priceEl = priceBlock.querySelector('.price');
    let compareEl = priceBlock.querySelector('.compare-price');
    let badgeEl = priceBlock.querySelector('.discount-badge');
    let badgeMobile = this.refs.stickyBar.querySelector('#sticky-badge-mobile');

    if (priceEl) {
      priceEl.dataset.moneyCents = String(info.price);
      priceEl.textContent = this.#formatMoney(info.price);
    }

    if (info.hasSale && container) {
      if (!compareEl) {
        compareEl = this.#createPricePart(container, 'compare-price', priceEl);
      }

      compareEl.dataset.moneyCents = String(info.compare);
      compareEl.textContent = this.#formatMoney(info.compare);
      compareEl.classList.remove('sticky-hidden');
      compareEl.style.display = '';
    } else if (compareEl) {
      compareEl.textContent = '';
      compareEl.classList.add('sticky-hidden');
      compareEl.style.display = 'none';
    }

    if (info.discountPct > 0 && container) {
      if (!badgeEl) {
        badgeEl = this.#createPricePart(container, 'discount-badge', compareEl || priceEl);
      }

      badgeEl.textContent = `${info.discountPct}% OFF`;
      badgeEl.classList.remove('sticky-hidden');
      badgeEl.style.display = '';
    } else if (badgeEl) {
      badgeEl.classList.add('sticky-hidden');
      badgeEl.style.display = 'none';
    }

    if (info.discountPct > 0) {
      if (!badgeMobile) {
        badgeMobile = this.#createMobileBadge();
      }

      if (badgeMobile) {
        badgeMobile.textContent = `${info.discountPct}% OFF`;
        badgeMobile.classList.remove('sticky-hidden');
      }
    } else if (badgeMobile) {
      badgeMobile.classList.add('sticky-hidden');
    }
  }

  /**
   * @param {Element} container
   * @param {string} className
   * @param {Element | null} afterElement
   */
  #createPricePart(container, className, afterElement) {
    const element = document.createElement('span');
    element.className = className;

    if (afterElement) {
      afterElement.insertAdjacentElement('afterend', element);
    } else {
      container.appendChild(element);
    }

    return element;
  }

  #createMobileBadge() {
    const titleRow = this.refs.stickyBar.querySelector('.sticky-bar__title-row');
    if (!titleRow) return null;

    const badge = document.createElement('span');
    badge.className = 'sticky-bar__badge sticky-bar__badge--mobile';
    badge.id = 'sticky-badge-mobile';
    titleRow.appendChild(badge);

    return badge;
  }

  /**
   * @param {Record<string, unknown>} variant
   * @param {number} discount
   */
  #calcPriceInfo(variant, discount) {
    const originalPrice = Number(variant.price);
    let finalPrice = originalPrice;
    let finalCompare = 0;
    let hasSale = false;
    let discountPct = 0;

    if (discount > 0) {
      finalPrice = Math.round((originalPrice * (100 - discount) / 100) / 100) * 100;
      finalCompare = originalPrice;
      hasSale = true;
      discountPct = discount;
    } else if (variant.compare_at_price && Number(variant.compare_at_price) > originalPrice) {
      finalPrice = originalPrice;
      finalCompare = Number(variant.compare_at_price);
      hasSale = true;
      discountPct = Math.round(((finalCompare - finalPrice) * 100) / finalCompare);
    }

    return { price: finalPrice, compare: finalCompare, hasSale, discountPct };
  }

  /**
   * @param {number} cents
   */
  #formatMoney(cents) {
    const formatter = new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
    return `₹${formatter.format(cents / 100)}`;
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
